"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import annotationPlugin, { AnnotationOptions } from "chartjs-plugin-annotation";
import { LoginLink } from "@kinde-oss/kinde-auth-nextjs";
import { createClient } from "@/utils/supabase/client";
import { VisaSlot } from "@/app/lib/types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface StatPageClientProps {
  initialSlots: VisaSlot[];
  uniqueCountries: string[];
  uniqueVisaTypes: string[];
  initialFilter: {
    country: string;
    visaType: string;
    tcn: string;
    horizontalLine: number;
    shouldShowAllData: boolean;
    bucket: number;
  };
}

const StatPageClient: React.FC<StatPageClientProps> = ({
  initialSlots,
  uniqueCountries,
  uniqueVisaTypes,
  initialFilter,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [slots, setSlots] = useState<VisaSlot[]>(initialSlots);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const tcnOptions = ["yes", "no"];

  const [country, setCountry] = useState(initialFilter.country);
  const [visaType, setVisaType] = useState(initialFilter.visaType);
  const [tcn, setTcn] = useState(initialFilter.tcn);
  const [horizontalLine, setHorizontalLine] = useState(
    initialFilter.horizontalLine
  );
  const [shouldShowAllData, setShouldShowAllData] = useState(
    initialFilter.shouldShowAllData
  );
  const [selectedBucket, setSelectedBucket] = useState(initialFilter.bucket);

  // Handle client-side mounting
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Only update URL on client-side after component is mounted
    if (!mounted) return;
    
    const params = new URLSearchParams(searchParams?.toString());
    params.set("country", country);
    params.set("visaType", visaType);
    params.set("tcn", tcn);
    params.set("horizontalLine", horizontalLine.toString());
    params.set("shouldShowAllData", shouldShowAllData.toString());
    params.set("bucket", selectedBucket.toString());
    router.push(`/stat?${params.toString()}`, { scroll: false });
  }, [
    country,
    visaType,
    tcn,
    horizontalLine,
    shouldShowAllData,
    selectedBucket,
    mounted,
    router,
    searchParams,
  ]);

  useEffect(() => {
    // Only fetch data on client-side after component is mounted
    if (!mounted) return;

    const fetchData = async () => {
      setIsLoading(true);
      const supabase = createClient();
      const bucketStart = new Date(
        Date.now() - (selectedBucket + 1) * 6 * 60 * 60 * 1000
      );
      const bucketEnd = new Date(
        Date.now() - selectedBucket * 6 * 60 * 60 * 1000
      );

      const { data, error } = await supabase
        .from("visaslot")
        .select("*")
        .gte("created_at", bucketStart.toISOString())
        .lt("created_at", bucketEnd.toISOString())
        .eq("country", country)
        .eq("type", visaType)
        .eq("tcn", tcn);

      if (error) {
        console.error("Error fetching data:", error);
      } else {
        setSlots(data || []);
      }
      setIsLoading(false);
    };

    fetchData();
  }, [country, visaType, tcn, selectedBucket, mounted]);

  const filteredSlots = useMemo(() => {
    return slots.filter(
      (slot) =>
        slot.country === country && slot.type === visaType && slot.tcn === tcn
    );
  }, [slots, country, visaType, tcn]);

  const cities = Array.from(new Set(filteredSlots.map((slot) => slot.city)));

  const createChartData = (citySlots: VisaSlot[]) => {
    const dataToShow = shouldShowAllData
      ? citySlots
      : citySlots.filter((slot) => {
          const waitingDays = Math.round(
            (new Date(slot.date).getTime() -
              new Date(slot.created_at).getTime()) /
              (1000 * 60 * 60 * 24)
          );
          return waitingDays < horizontalLine;
        });

    dataToShow.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    return {
      labels: dataToShow.map((slot, index) =>
        `Point ${index + 1}`
      ),
      datasets: [
        {
          label: "Waiting(days)",
          data: dataToShow.map((slot) => {
            const waitingDays = Math.round(
              (new Date(slot.date).getTime() -
                new Date(slot.created_at).getTime()) /
                (1000 * 60 * 60 * 24)
            );
            return waitingDays;
          }),
          backgroundColor: dataToShow.map((slot) => {
            const waitingDays = Math.round(
              (new Date(slot.date).getTime() -
                new Date(slot.created_at).getTime()) /
                (1000 * 60 * 60 * 24)
            );
            return waitingDays < horizontalLine
              ? "rgba(75, 192, 192, 0.6)"
              : "rgba(255, 99, 132, 0.6)";
          }),
          borderColor: dataToShow.map((slot) => {
            const waitingDays = Math.round(
              (new Date(slot.date).getTime() -
                new Date(slot.created_at).getTime()) /
                (1000 * 60 * 60 * 24)
            );
            return waitingDays < horizontalLine
              ? "rgb(75, 192, 192)"
              : "rgb(255, 99, 132)";
          }),
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Waiting Days",
        },
      },
      x: {
        title: {
          display: true,
          text: "Fetch Time",
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const dataIndex = context.dataIndex;
            const dataset = context.dataset;
            const waitingDays = dataset.data[dataIndex] as number;
            const earliestDate =
              new Date().getTime() + waitingDays * 24 * 60 * 60 * 1000;

            return [
              `Waiting Days: ${waitingDays}`,
              `Earliest Date: ${new Date(earliestDate).toDateString()}`,
            ];
          },
        },
      },
      annotation: {
        annotations: {
          line1: {
            type: "line",
            yMin: horizontalLine,
            yMax: horizontalLine,
            borderColor: "rgb(255, 0, 0)",
            borderWidth: 2,
          } as AnnotationOptions<"line">,
        },
      },
    },
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl md:text-3xl font-bold text-center mb-6">
        Historical Visa Slot Data
      </h1>
      
      {/* Mobile-friendly form controls */}
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              {uniqueCountries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visa Type
            </label>
            <select
              value={visaType}
              onChange={(e) => setVisaType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              {uniqueVisaTypes.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              TCN Status
            </label>
            <select
              value={tcn}
              onChange={(e) => setTcn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              {tcnOptions.map((t) => (
                <option key={t} value={t}>
                  {t === "yes" ? "Yes" : "No"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Bucket
            </label>
            <select
              value={selectedBucket}
              onChange={(e) => setSelectedBucket(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              {Array.from({ length: 100 }, (_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? "Latest" : `${i * 6} hours ago`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Horizontal Line (days)
            </label>
            <input
              type="number"
              value={horizontalLine}
              onChange={(e) => setHorizontalLine(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="Enter number of days"
            />
          </div>

          <div className="flex items-center justify-start md:justify-end">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={shouldShowAllData}
                onChange={(e) => setShouldShowAllData(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700">Show all data</span>
            </label>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {cities.map((city) => {
            const citySlots = filteredSlots.filter(
              (slot) => slot.city === city
            );
            const chartData = createChartData(citySlots);
            
            return (
              <div
                key={city}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6"
              >
                <h2 className="text-lg md:text-xl font-semibold mb-4 text-center">
                  {city}
                </h2>
                <div className="h-64 md:h-80">
                  <Bar data={chartData} options={chartOptions} />
                </div>
                <div className="mt-4 text-center">
                  <LoginLink
                    postLoginRedirectURL="/app"
                    className="inline-block bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-2 px-4 md:px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 text-sm md:text-base"
                  >
                    Get Instant Visa Alerts
                  </LoginLink>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatPageClient;
